import { useEffect, useState, useRef } from 'react';
import { InputAdornment } from '@mui/material';
import { NotificationType } from '../../../config';
import itemService from '../../../service/item.service';
import addNotification from '../../../utils/addNotification';
import { AutoCompleteSelectUI, CheckboxUI, FormErrorUI, FormLabelUI, InputUI } from '../../Interface';
import { Field, useFormikContext } from 'formik';
import useConfig from '../../../context/ConfigContext';
import useParam from '../../../context/ParamContext';
import {
  buildItemCodeRequest,
  buildItemCodePolicyRequest,
  getDefaultCustomItemCodePolicy,
  getManualItemCodeSuffix,
  getProtectedManualItemCode,
  isCustomItemCodeGenerationEnabled,
  normalizeGeneratedItemCode,
} from '../itemCodeGeneration';

const ItemAddDialogFields = ({ autoCompleteOptions, autoCompleteData }) => {
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const { config, getConfig } = useConfig();
  const { params, getParam } = useParam();
  const [itemCodePolicy, setItemCodePolicy] = useState(null);
  const itemCodeGenerationStateRef = useRef({
    itemGroupId: undefined,
    autoGenerationEnabled: false,
  });
  const customGenerateRef = useRef({ lastPolicyKey: null, inProgress: false });
  const isCustomItemCodeGeneration = isCustomItemCodeGenerationEnabled(config);
  const effectiveItemCodePolicy = itemCodePolicy ?? getDefaultCustomItemCodePolicy();
  const shouldAutoGenerateItemCode = isCustomItemCodeGeneration
    ? values.autoGenerateItemCode === true
    : params.GENERATE_ITEMCODE === 'Y';
  const shouldShowAutoGenerateToggle =
    isCustomItemCodeGeneration && Boolean(effectiveItemCodePolicy.autoGenerateVisible);
  const isAutoGenerateLocked =
    Boolean(effectiveItemCodePolicy.autoGenerateLocked) || effectiveItemCodePolicy.manualAllowed === false;
  const manualPrefix =
    isCustomItemCodeGeneration && !shouldAutoGenerateItemCode ? effectiveItemCodePolicy.manualPrefix ?? '' : '';
  const isManualPrefixMode = Boolean(manualPrefix);
  const displayItemCode = isManualPrefixMode
    ? getManualItemCodeSuffix(values.itemCode ?? '', manualPrefix)
    : values.itemCode ?? '';
  const customPolicyRequest = buildItemCodePolicyRequest(values);
  const customPolicyRequestKey = JSON.stringify(customPolicyRequest);
  const customGenerateRequestKey = JSON.stringify(buildItemCodeRequest({ ...values, itemCode: null }, true));

  useEffect(() => {
    if (!Object.keys(config).length) getConfig();
    if (!Object.keys(params).length) getParam();
  }, []);

  const handleGenerateItemCode = async (itemGroupId = values.itemGroupId ?? null) => {
    const response = await itemService.generateItemCode(itemGroupId);
    const generatedItemCode = normalizeGeneratedItemCode(response);

    if (!generatedItemCode) {
      addNotification({
        content: 'A cikkszám generálása sikertelen!',
        type: NotificationType.ERROR,
      });
      return;
    }

    await setFieldValue('itemCode', generatedItemCode);
    validateField('itemCode');
  };

  const handleGenerateItemCodeByPolicy = async () => {
    const response = await itemService.generateItemCodeByStrategy(
      buildItemCodeRequest({ ...values, itemCode: null }, true)
    );
    const generatedItemCode = normalizeGeneratedItemCode(response);

    if (!generatedItemCode) {
      addNotification({
        content: 'A cikkszám generálása sikertelen!',
        type: NotificationType.ERROR,
      });
      return;
    }

    await setFieldValue('itemCode', generatedItemCode, false);
    validateField('itemCode');
  };

  const handleItemCodeChange = (event) => {
    const nextValue = event.target.value ?? '';
    const protectedValue = isManualPrefixMode ? getProtectedManualItemCode(nextValue, manualPrefix) : nextValue;

    setFieldValue('itemCode', protectedValue, false);
  };

  useEffect(() => {
    if (!isCustomItemCodeGeneration) {
      setItemCodePolicy(null);
      return undefined;
    }

    if (!values.itemGroupId) {
      setItemCodePolicy(getDefaultCustomItemCodePolicy());
      return undefined;
    }

    let active = true;

    itemService
      .getItemCodePolicy(customPolicyRequest)
      .then((policy) => {
        if (!active) {
          return;
        }

        const resolvedPolicy = policy ?? getDefaultCustomItemCodePolicy();

        setItemCodePolicy(resolvedPolicy);

        if (
          (resolvedPolicy.autoGenerateLocked || resolvedPolicy.manualAllowed === false) &&
          values.autoGenerateItemCode !== true
        ) {
          if (values.itemCode) {
            setFieldValue('itemCode', '', false);
          }
          setFieldValue('autoGenerateItemCode', true, false);
          return;
        }

        if (values.autoGenerateItemCode === null || values.autoGenerateItemCode === undefined) {
          const shouldKeepManualItemCode =
            Boolean(values.itemCode) &&
            resolvedPolicy.autoGenerateLocked !== true &&
            resolvedPolicy.manualAllowed !== false;

          setFieldValue(
            'autoGenerateItemCode',
            shouldKeepManualItemCode ? false : resolvedPolicy.autoGenerateDefault,
            false
          );
        }
      })
      .catch(() => {
        if (active) {
          setItemCodePolicy(getDefaultCustomItemCodePolicy());
        }
      });

    return () => {
      active = false;
    };
  }, [customPolicyRequestKey, isCustomItemCodeGeneration, setFieldValue, values.itemGroupId]);

  useEffect(() => {
    if (isCustomItemCodeGeneration) {
      if (!shouldAutoGenerateItemCode) {
        customGenerateRef.current.lastPolicyKey = null;
        const protectedValue =
          manualPrefix && !(values.itemCode ?? '').startsWith(manualPrefix) ? manualPrefix : values.itemCode ?? '';
        if (protectedValue !== (values.itemCode ?? '')) {
          setFieldValue('itemCode', protectedValue, false);
        }
        return;
      }
      // Prevent repeated generation loops: only generate when necessary
      const run = async () => {
        if (customGenerateRef.current.inProgress) return;
        const policyChanged = customGenerateRef.current.lastPolicyKey !== customGenerateRequestKey;
        if (values.itemCode && !policyChanged) return;

        try {
          customGenerateRef.current.inProgress = true;
          await handleGenerateItemCodeByPolicy();
          customGenerateRef.current.lastPolicyKey = customGenerateRequestKey;
        } finally {
          customGenerateRef.current.inProgress = false;
        }
      };

      run();
      return;
    }

    const previousState = itemCodeGenerationStateRef.current;
    const itemGroupChanged = previousState.itemGroupId !== values.itemGroupId;
    const autoGenerationEnabledNow = params.GENERATE_ITEMCODE === 'Y' && !previousState.autoGenerationEnabled;

    itemCodeGenerationStateRef.current = {
      itemGroupId: values.itemGroupId,
      autoGenerationEnabled: params.GENERATE_ITEMCODE === 'Y',
    };

    if ((itemGroupChanged || autoGenerationEnabledNow) && !values.itemCode && params.GENERATE_ITEMCODE === 'Y') {
      handleGenerateItemCode(values.itemGroupId ?? null);
    }
  }, [
    customGenerateRequestKey,
    isCustomItemCodeGeneration,
    manualPrefix,
    params.GENERATE_ITEMCODE,
    setFieldValue,
    shouldAutoGenerateItemCode,
    values.itemCode,
    values.itemGroupId,
  ]);

  return (
    <div className="grid grid-cols-6 gap-x-4 gap-y-2 lg:mx-4 mb-5 md:mt-2">
      <div className="col-span-3">
        <InputUI
          fullWidth
          name="itemCode"
          type="text"
          value={displayItemCode}
          onChange={handleItemCodeChange}
          onBlur={() => validateField('itemCode')}
          label={<FormLabelUI text="Cikkszám" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.itemCode} />}
          disabled={shouldAutoGenerateItemCode}
          InputLabelProps={{ shrink: values.itemCode !== '' }}
          InputProps={
            isManualPrefixMode
              ? {
                  startAdornment: <InputAdornment position="start">{manualPrefix}</InputAdornment>,
                }
              : undefined
          }
        />
      </div>
      <div className="col-span-3">
        <Field
          type="text"
          name="itemName"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Cikknév" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.itemName} />}
          InputLabelProps={{ shrink: values.itemName !== '' }}
        />
      </div>
      {shouldShowAutoGenerateToggle && (
        <div className="col-span-3 -mt-1">
          <CheckboxUI
            name="autoGenerateItemCode"
            checked={Boolean(shouldAutoGenerateItemCode)}
            onCheckedChange={(checked) => {
              setFieldValue('autoGenerateItemCode', checked, false);

              if (checked) {
                setFieldValue('itemCode', '', false);
              } else {
                const nextItemCode =
                  effectiveItemCodePolicy.manualPrefix &&
                  !(values.itemCode ?? '').startsWith(effectiveItemCodePolicy.manualPrefix)
                    ? effectiveItemCodePolicy.manualPrefix
                    : values.itemCode ?? '';
                setFieldValue('itemCode', nextItemCode, false);
              }
            }}
            label={<FormLabelUI text="Cikkszám generálás" />}
            disabled={isAutoGenerateLocked}
          />
        </div>
      )}
      <div className="col-span-3">
        <Field
          type="autocomplete"
          name="itemGroupId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Cikkcsoport" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.itemGroupId} />}
          onChange={(_e, newVal) => {
            const findedGroup = autoCompleteData.itemGroup.find((i) => i.id === newVal?.value);
            const findedME = autoCompleteData.quantityUnit.find((i) => findedGroup?.quantityUnitId === i?.id);

            setFieldValue('weight', findedME?.weight ?? '');
            setFieldValue('quantityUnitId', findedGroup?.quantityUnitId ?? null);
            setFieldValue('type', findedGroup?.type ?? null);
            setFieldValue('landedCost', false);
            setFieldValue('stockItemType', findedGroup?.stockItemType ?? null);
            setFieldValue('inPurchase', findedGroup?.inPurchase ?? null);
            setFieldValue('inSales', findedGroup?.inSales ?? null);
            setFieldValue('autoGenerateItemCode', null, false);
            setFieldValue('itemGroupId', newVal?.value ?? null).then(() => {
              validateField('itemGroupId');
            });
            // If auto-generation is enabled, clear current itemCode when item group changes
            if (shouldAutoGenerateItemCode) {
              setFieldValue('itemCode', '', false);
            }
          }}
          InputLabelProps={{ shrink: values.itemGroupId !== '' }}
          selectedValue={values.itemGroupId}
          dataset={autoCompleteOptions.itemGroup}
        />
      </div>
    </div>
  );
};

export default ItemAddDialogFields;
